(ns ui.action-handlers
  "Register Replicant action handlers here."
  (:require [app.client-log :as client-log]
            [parts.replicant.command :as command]
            [parts.replicant.query :as query]
            [parts.replicant.command-query :as command-query]
            [goog.object :as obj]
            [app.util.transit :as transit]
            [ui.crisp :as crisp]
            [ui.error-alert]
            [app.agenda.home :as home-page]
            [ui.totp.core :as core]
            [ui.totp.page]
            [ui.totp.new]
            [ui.totp.relation]
            [ui.elevenlabs.page]
            [ui.dall-e.page]
            [ui.editor2.timeline]
            [ui.editor2.actions]
            [cljs.core.async :refer [<!] :as a]
            [clojure.string :as str]
            [storrito.editor.image :as editor-image]
            [storrito.image :as image]
            [storrito.authentication.return-to :as return-to]
            [ui.gallery.link :as gallery-link]
            [ui.upload-status :as upload-status]
            [ui.gallery.actions :as gallery-actions]
            [storrito.connect3.core]
            [storrito.upload.story-upload :as story-upload]
            [storrito.state.local-storage :as local-storage]
            [lambdaisland.uri :as uri]))

(defn download-image!
  [image-url filename]
  (-> (js/fetch image-url)
      (.then (fn [response]
               (if (.-ok response)
                 (.blob response)
                 (throw (js/Error.
                          (str "Fetch failed with status "
                               (.-status response)))))))
      (.then (fn [blob]
               (let [blob-url (js/URL.createObjectURL blob)
                     a (.createElement js/document "a")]
                 (.setAttribute a "href" blob-url)
                 (.setAttribute a "download" filename)
                 (set! (.-style.display a) "none")
                 (.appendChild (.-body js/document) a)
                 (.click a)
                 (.removeChild (.-body js/document) a)
                 (js/setTimeout
                   (fn []
                     (js/URL.revokeObjectURL blob-url))
                   500))))))

(defn copy-with-text-area!
  "Copies `text` via a hidden textarea and `execCommand`, for when the
   clipboard API is missing or refuses to write. Returns true when the
   browser copied the text."
  [text]
  (let [field (.createElement js/document "textarea")]
    (set! (.-value field) text)
    (set! (.. field -style -position) "fixed")
    (set! (.. field -style -opacity) "0")
    (.appendChild (.-body js/document) field)
    (.select field)
    (let [copied (try
                   (.execCommand js/document "copy")
                   (catch :default _
                     false))]
      (.removeChild (.-body js/document) field)
      copied)))

(defn copy-to-clipboard!
  "Copies `text` to the clipboard. Returns a promise of true when the text
   was copied; it never rejects. `writeText` rejects with a
   `NotAllowedError` when the browser denies the write permission (a
   policy or a site setting), then the textarea is the fallback.
   Sentry: STORRITO-V2-KY."
  [text]
  (if-let [clipboard (.-clipboard js/navigator)]
    (-> (.writeText clipboard text)
        (.then (constantly true))
        (.catch (fn [_]
                  (copy-with-text-area! text))))
    (js/Promise.resolve (copy-with-text-area! text))))

(def action-handlers
  (concat
    ui.editor2.actions/registry-entries
    [{:ui.action/kind :data/repeat
      :ui.action/handler (fn repeat [params]
                           (let [[_ delays actions] (:action params)]
                             (when-let [delay (first delays)]
                               (js/setTimeout
                                 (fn []
                                   ((:ui/event-handler params)
                                    {}
                                    actions)
                                   (-> params
                                       (update-in [:action 1]
                                                  rest)
                                       (repeat)))
                                 delay))))}

     {:ui.action/kind :ui/show-error
      :ui.action/handler ui.error-alert/show-error}

     {:ui.action/kind :totp/refresh
      :ui.action/handler (fn totp-refresh [{:keys [store ui/event-handler]}]
                           (swap! store assoc :ui/clock (js/Date.))
                           (when-not (:totp/interval-id @store)
                             (swap! store
                                    assoc
                                    :totp/interval-id
                                    (js/setInterval
                                      (fn []
                                        (let [page-id (get-in @store [:location :location/page-id])]
                                          (cond
                                            (#{:pages/totp :pages/totp-new} page-id)
                                            (do
                                              (swap! store assoc :ui/clock (js/Date.))
                                              (let [expire-date (:totp/expire-timestamp (query/get-result @store
                                                                                                          core/totp-query))
                                                    now (js/Date.)]
                                                (when (< expire-date now)
                                                  (event-handler {} [[:data/query core/totp-query]]))))

                                            ;; The connect3 TOTP preview only needs the ticking
                                            ;; clock for its countdown, no query refresh:
                                            (#{:pages/connect3 :pages/connect3-redirect} page-id)
                                            (swap! store assoc :ui/clock (js/Date.))

                                            (= :pages/instagram-connect page-id)
                                            (do
                                              (swap! store assoc :ui/clock (js/Date.))
                                              (let [expire-date (:totp/expire-timestamp
                                                                  (query/get-result @store
                                                                                    {:query/kind :query/instagram-connect}))
                                                    now (js/Date.)]
                                                (when (and expire-date (< expire-date now))
                                                  (event-handler {} [[:data/query {:query/kind :query/instagram-connect}]]))))

                                            :else
                                            (do
                                              (js/clearInterval (:totp/interval-id @store))
                                              (swap! store dissoc :totp/interval-id)))))
                                      1000))))}

     ;; Fire-and-forget funnel/diagnostic logging as a data action, so
     ;; click handlers can log declaratively:
     ;; [:client-log/log :onboarding/connect-skipped]
     ;; [:client-log/log :some/event {:some "data"}]
     {:ui.action/kind :client-log/log
      :ui.action/handler (fn [{:keys [action]}]
                           (let [[_ event data] action]
                             (client-log/log! event (or data {}))))}

     {:ui.action/kind :crisp/open-chat
      :ui.action/handler (fn [_]
                           (.push js/window.$crisp
                                  (clj->js ["do"
                                            "chat:open"])))}

     {:ui.action/kind :crisp/set-message-text
      :ui.action/handler (fn [{:keys [action]}]
                           (let [[_ message] action]
                             (.push js/window.$crisp
                                    (clj->js ["set"
                                              "message:text"
                                              [message]]))))}

     {:ui.action/kind :connect3/send!
      :ui.action/handler storrito.connect3.core/send!}

     {:ui.action/kind :connect3/validate-twofacode
      :ui.action/handler storrito.connect3.core/validate-twofacode}

     {:ui.action/kind :connect3/init
      :ui.action/handler storrito.connect3.core/init}

     {:ui.action/kind :connect3/start-recovery
      :ui.action/handler storrito.connect3.core/start-recovery!}

     ;; New Audio playback action-handlers:
     ;; :audio/toggle will either pause the audio if it's already playing or
     ;; reset/restart playback from the beginning.
     {:ui.action/kind :audio/toggle
      :ui.action/handler (fn [{:keys [action store audio-player]}]
                           (let [[_ url] action]
                             (if (and (= url (.-src audio-player))
                                      (not (.-paused audio-player)))
                               (do
                                 (.pause audio-player)
                                 (swap! store dissoc :audio/current-url))
                               (do
                                 (.pause audio-player)      ; ensure a clean start
                                 (set! (.-src audio-player) url)
                                 (set! (.-currentTime audio-player) 0)
                                 (-> (.play audio-player)
                                     (.catch (fn [e]
                                               (when-not (= "NotAllowedError" (.-name e))
                                                 (js/console.warn "Audio playback failed:" e)))))
                                 (swap! store assoc :audio/current-url url)))))}

     ;; Explicit play action.
     {:ui.action/kind :audio/play
      :ui.action/handler (fn [{:keys [action store audio-player]}]
                           (let [[_ url] action]
                             (.pause audio-player)
                             (set! (.-src audio-player) url)
                             (set! (.-currentTime audio-player) 0)
                             (-> (.play audio-player)
                                 (.catch (fn [e]
                                           (when-not (= "NotAllowedError" (.-name e))
                                             (js/console.warn "Audio playback failed:" e)))))
                             (swap! store assoc :audio/current-url url)))}

     ;; Explicit pause action.
     {:ui.action/kind :audio/pause
      :ui.action/handler (fn [{:keys [store audio-player]}]
                           (.pause audio-player)
                           (swap! store dissoc :audio/current-url))}

     {:ui.action/kind :ui/modal-close
      :ui.action/handler (fn [{:keys [store ui/event-handler ui/get-register]}]
                           ;; Let the closing modal clean up (e.g. stop the
                           ;; music preview playback) via its :on-unload:
                           (when-let [modal-id (:modal-id (:ui/modal @store))]
                             (when-let [on-unload (some (fn [entry]
                                                          (when (= (:modal-id entry)
                                                                   modal-id)
                                                            (:on-unload entry)))
                                                        (get-register))]
                               (when-let [actions (on-unload @store)]
                                 (event-handler {} actions))))
                           (swap! store
                                  dissoc
                                  :ui/modal
                                  false))}

     {:ui.action/kind :ui/modal-open
      :ui.action/handler (fn [{:keys [action store ui/event-handler ui/get-register]}]
                           (let [[_ modal-id params] action]
                             (when-let [modal (some
                                                (fn [entry]
                                                  (when (= (:modal-id entry)
                                                           modal-id)
                                                    entry))
                                                (get-register))]
                               (swap! store
                                      assoc
                                      :ui/modal
                                      {:modal-id modal-id
                                       :params params
                                       :render (:render modal)}
                                      )
                               (when-let [on-load (:on-load modal)]
                                 (event-handler {}
                                                (on-load @store))))))}

     {:ui.action/kind :editor/add-image
      :ui.action/handler (fn [{:keys [action]}]
                           (let [[_ blob-uuid] action]
                             (js/console.log "Add Blob to Editor : " (pr-str blob-uuid))
                             (<! (editor-image/add-image-from-url!
                                   (image/url blob-uuid)))))}

     {:ui.action/kind :blob/auto-download
      :ui.action/handler (fn [{:keys [action store]}]
                           (let [[_ params] action
                                 result (command/get-latest-result @store (:command params))
                                 blob-uuid (get-in result [:blob-uuid])
                                 element (js/document.createElement "a")
                                 blob-url (image/url blob-uuid)]
                             (js/console.log "Auto Download Blob : " (pr-str blob-uuid))
                             (obj/set element
                                      "href"
                                      blob-url)
                             (obj/set element
                                      "download"
                                      blob-uuid)
                             (obj/set element
                                      "style"
                                      #js {:display "none"})
                             (js/document.body.appendChild element)
                             (.click element)
                             (js/document.body.removeChild element)
                             (js/window.URL.revokeObjectURL blob-url)))}

     {:ui.action/kind :url/download
      :ui.action/handler (fn [{:keys [action]}]
                           (let [[_ blob-url filename] action]
                             (download-image! blob-url
                                              (or filename
                                                  "ai-image.jpg"))))}

     {:ui.action/kind :auth/check
      :ui.action/handler (fn [w]
                           ;; Redirects the user to the login page if they are not logged in.
                           ;; Skip redirect when a connect-link param is present, since
                           ;; external users open connect-links without a Storrito account.
                           (when-not (:connect-link (uri/query-map (uri/uri js/window.location.href)))
                             (let [query {:query/kind :query/user-session}]
                               (.then
                                 (command-query/query-backend
                                   (assoc w
                                     :action [:data/query query]))
                                 (fn [state]
                                   (let [result (query/get-result state query)]
                                     (if-not (:organization/uuid result)
                                       ;; Keep the requested page (e.g. an onboarding
                                       ;; email's /ui/subscription link) so the login
                                       ;; flow returns there instead of the agenda.
                                       (.replace js/window.location
                                                 (return-to/login-url
                                                   (str js/window.location.pathname
                                                        js/window.location.search)))
                                       (do
                                         (when (and (exists? js/Sentry)
                                                    (:user/email result))
                                           (js/Sentry.onLoad
                                             (fn []
                                               (js/Sentry.setUser
                                                 #js {:email (:user/email result)
                                                      :free-account (:organization/free-account? result)
                                                      :legacy-customer (:organization/legacy-customer? result)
                                                      :positive-credit-balance (:organization/positive-credit-balance? result)}))))
                                         (crisp/identify! result)))))))))}


     {:ui.action/kind :subscription/redirect-if-legacy
      :ui.action/handler (fn [w]
                           ;; Redirects legacy customers to the legacy subscription page.
                           (let [query {:query/kind :query/user-session}]
                             (.then
                               (command-query/query-backend
                                 (assoc w
                                   :action [:data/query query]))
                               (fn [state]
                                 (when (:organization/legacy-subscription-id
                                         (query/get-result state query))
                                   (.replace js/window.location "/ui/legacy-subscription"))))))}

     {:ui.action/kind :legacy-subscription/redirect-if-not-legacy
      :ui.action/handler (fn [w]
                           ;; Redirects non-legacy customers to the subscription page.
                           (let [query {:query/kind :query/user-session}]
                             (.then
                               (command-query/query-backend
                                 (assoc w
                                   :action [:data/query query]))
                               (fn [state]
                                 (when-not (:organization/legacy-subscription-id
                                             (query/get-result state query))
                                   (.replace js/window.location "/ui/subscription"))))))}

     {:ui.action/kind :ig-account-modal/copy-otp
      :ui.action/handler (fn [{:keys [action]}]
                           (let [[_ text] action]
                             (copy-to-clipboard! text)))}

     {:ui.action/kind :clipboard/copy
      :ui.action/handler (fn [{:keys [store action]}]
                           (let [[_ text store-key] action]
                             (-> (copy-to-clipboard! text)
                                 (.then (fn [copied]
                                          (when copied
                                            (swap! store assoc store-key true)
                                            (js/setTimeout
                                              (fn [] (swap! store dissoc store-key))
                                              1500)))))))}

     ;; The question is asked in the modal
     ;; (`ui.instagram-connect.account-modal/delete-account-footer`), not
     ;; through `js/confirm`, which a browser-control session cannot answer
     ;; (brain-129). A failed request is shown there instead of only on
     ;; the console, so a hanging or failing delete is visible.
     {:ui.action/kind :ig-account-modal/delete-account
      :ui.action/handler (fn [{:keys [store action]}]
                           (let [[_ ig-user-id] action
                                 fail! (fn [err]
                                         (js/console.warn "Failed to delete Instagram account:" err)
                                         (swap! store assoc
                                                :ui.instagram-connect.account-modal/deleting? false
                                                :ui.instagram-connect.account-modal/delete-error
                                                "Deleting the connection failed. Please try again or contact support."))]
                             (swap! store assoc
                                    :ui.instagram-connect.account-modal/deleting? true
                                    :ui.instagram-connect.account-modal/delete-error nil)
                             (-> (js/fetch "/connect/ig-connect/delete"
                                           #js {:method "POST"
                                                :headers #js {"Content-Type" "application/transit+json"}
                                                :body (transit/transit-encode
                                                        {:ig-user-id ig-user-id})})
                                 (.then (fn [response]
                                          (if (.-ok response)
                                            (do
                                              (swap! store dissoc
                                                     :ui/modal
                                                     :ui.instagram-connect.account-modal/deleting?
                                                     :ui.instagram-connect.account-modal/confirm-delete?)
                                              (.replace js/window.location "/ui/instagram-connect"))
                                            (fail! (str "HTTP " (.-status response))))))
                                 (.catch fail!))))}

     {:ui.action/kind :ig-account-modal/sync
      :ui.action/handler (fn [{:keys [store action ui/event-handler]}]
                           (let [[_ ig-user-id] action]
                             (swap! store assoc :ui.instagram-connect.account-modal/syncing true)
                             (-> (js/fetch "/api/instagram-info"
                                           #js {:method "POST"
                                                :headers #js {"Content-Type" "application/transit+json"}
                                                :body (transit/transit-encode
                                                        {:ig-user-id ig-user-id
                                                         :manually-sync true})})
                                 (.then (fn [_]
                                          (swap! store dissoc :ui.instagram-connect.account-modal/syncing)
                                          (event-handler {} [[:data/query {:query/kind :query/instagram-connect}]])))
                                 (.catch (fn [err]
                                           (swap! store dissoc :ui.instagram-connect.account-modal/syncing)
                                           (js/console.warn "Failed to sync Instagram account:" err))))))}

     {:ui.action/kind :upload/pick-files
      :ui.action/handler
      (fn [{:keys [store] :as w}]
        (let [input (js/document.createElement "input")]
          (set! (.-type input) "file")
          (set! (.-multiple input) true)
          (set! (.-accept input) ".jpg,.jpeg,image/png,video/*,.mov")
          (set! (.-style.display input) "none")
          (.addEventListener input "change"
            (fn [e]
              (let [file-list (.. e -target -files)
                    files (vec (for [i (range (.-length file-list))]
                                 (.item file-list i)))]
                (when (seq files)
                  (let [batch (upload-status/start! store (count files))
                        stop-tracking! (upload-status/track-uploads! store)]
                    (a/take! (story-upload/handle-quick-story-files!
                               files
                               nil
                               {:on-checked (fn [{:keys [rejected]}]
                                              (upload-status/checked! store batch rejected))
                                :on-error (fn [{:keys [message]}]
                                            (upload-status/failed! store batch message))})
                             (fn [result]
                               (stop-tracking!)
                               ;; The router keeps the store, so the card
                               ;; still lists the rejected files in the
                               ;; gallery:
                               (when (upload-status/show-gallery-after-upload?
                                       result
                                       js/window.location.pathname)
                                 (gallery-actions/navigate! w
                                                            (gallery-link/gallery-href)
                                                            false)))))))
              (.remove input)))
          (js/document.body.appendChild input)
          (.click input)))}

     {:ui.action/kind :ui/hide-sidebar-on-mobile
      :ui.action/handler
      (fn [{:keys [store]}]
        (when (< js/window.innerWidth 640)
          (swap! store assoc :ui/hide-side-menu? true)))}

     {:ui.action/kind :ig/restore-selection
      :ui.action/handler
      (fn [{:keys [store]}]
        (when-let [id (:instagram.user/id @local-storage/state)]
          (swap! store assoc
                 :app.agenda.home/selected-ig-user-id id
                 :app.agenda.home/selected-account-key (str "instagram:" id))))}

     {:ui.action/kind :schedule/restore-selection
      :ui.action/handler
      (fn [{:keys [store]}]
        (if-let [account-key (:schedule/account-key @local-storage/state)]
          (swap! store assoc :app.agenda.home/selected-account-key account-key)
          (when-let [id (:instagram.user/id @local-storage/state)]
            (swap! store assoc :app.agenda.home/selected-account-key (str "instagram:" id)))))}

     {:ui.action/kind :schedule/save-selection
      :ui.action/handler
      (fn [{:keys [action]}]
        (let [[_ account-key] action]
          ;; /ui/social-schedule must show the account picked here:
          ;; dropping its own remembered selection makes its restore fall
          ;; back to this account (the last selection wins).
          (swap! local-storage/state
                 #(-> %
                      (assoc :schedule/account-key account-key)
                      (dissoc :social-scheduling/selected-destinations)))
          (when (and account-key (str/starts-with? account-key "instagram:"))
            (swap! local-storage/state assoc
                   :instagram.user/id
                   (subs account-key (count "instagram:"))))))}

     {:ui.action/kind :ig/save-selection
      :ui.action/handler
      (fn [{:keys [action]}]
        (let [[_ id] action]
          ;; See :schedule/save-selection for the dissoc.
          (swap! local-storage/state
                 #(-> %
                      (assoc :instagram.user/id id
                             :schedule/account-key (str "instagram:" id))
                      (dissoc :social-scheduling/selected-destinations)))))}

     {:ui.action/kind :agenda/set-date
      :ui.action/handler
      (fn [{:keys [store action ui/event-handler]}]
        (let [[_ date] action]
          (swap! store assoc :app.agenda.home/agenda-date date)
          (event-handler {} [[:data/query (home-page/home-query @store)]])))}

     {:ui.action/kind :home/refresh
      :ui.action/handler
      (fn [{:keys [store ui/event-handler]}]
        (event-handler {} [[:data/query (home-page/home-query @store)]]))}

     {:ui.action/kind :store/update
      :ui.action/handler (fn [{:keys [action store]}]
                           (let [[_ f & args] action]
                             (apply swap! store f args)))}

     {:ui.action/kind :debounce/assoc
      :ui.action/handler
      (let [timeouts (atom {})]
        (fn [{:keys [action store]}]
          (let [[_ ms key value] action
                prev (get @timeouts key)]
            (when prev (js/clearTimeout prev))
            (swap! timeouts assoc
                   key
                   (js/setTimeout
                     (fn []
                       (swap! store assoc key value)
                       (swap! timeouts dissoc key))
                     ms)))))}

     {:ui.action/kind :js/prevent-default
      :ui.action/handler (fn [w]
                           (let [event (:replicant/js-event w)]
                             (.preventDefault event)))}
     {:ui.action/kind :js/stop-propagation
      :ui.action/handler (fn [w]
                           (let [event (:replicant/js-event w)]
                             (.stopPropagation event)))}
     {:ui.action/kind :js/set-drag-data
      :ui.action/handler (fn [{:keys [action] :as w}]
                           (let [event (:replicant/js-event w)
                                 [_ data-type] action]
                             ;; Firefox only starts a drag when data
                             ;; was set during the dragstart event. A
                             ;; custom `data-type` also tags the drag,
                             ;; so listeners can distinguish it from
                             ;; e.g. files dragged in from the OS:
                             (.setData (.-dataTransfer event)
                                       (or data-type "text/plain")
                                       "")))}
     {:ui.action/kind :calendar/navigate-to-publish
      :ui.action/handler (fn [{:keys [action]}]
                           (let [[_ story-eid] action]
                             (when story-eid
                               (set! js/location.href
                                     (str "/ui/story-publish?eid=" story-eid)))))}
     {:ui.action/kind :story/download-poll
      :ui.action/handler (fn download-poll [{:keys [store ui/event-handler]}]

                           ;; Timeout after 5 minutes
                           (js/setTimeout
                             (fn []
                               (js/console.log "STOP POLLING")
                               (js/clearInterval (:download/poll @store))
                               (swap! store dissoc :download/poll))
                             (* 1000 60 5))

                           (when-not (:download/poll @store)
                             (swap! store
                                    assoc
                                    :download/poll
                                    (js/setInterval
                                      (fn []
                                        (let [page-id (get-in @store [:location :location/page-id])]
                                          (if (= :pages/editor2 page-id)
                                            (event-handler {} [[:data/query {:query/kind :query/download-poll
                                                                             :query/data {:story-eid (:story-media/eid (:editor2 @store))}}]])
                                            (do
                                              (js/clearInterval (:download/poll @store))
                                              (swap! store dissoc :download/poll)))))
                                      2000))
                             )
                           )}

     {:ui.action/kind :subscription/watch-exit
      :ui.action/handler
      (fn [{:keys [store ui/event-handler]}]
        (when-not (:subscription/exit-watcher-added @store)
          (swap! store assoc :subscription/exit-watcher-added true)
          (add-watch store ::subscription-exit
              (fn [_ _ old-state new-state]
                (let [old-page (get-in old-state [:location :location/page-id])
                      new-page (get-in new-state [:location :location/page-id])]
                  (when (and (= old-page :pages/subscription)
                             (not= new-page :pages/subscription)
                             (not (:subscription/exit-modal-shown new-state))
                             (not (:ui/modal new-state))
                             (let [subscription (query/get-result
                                                  old-state
                                                  {:query/kind :query/subscription})
                                   status (get-in subscription
                                                  [:organization/paddle-subscription
                                                   :status])]
                               (not (#{"active" "past_due"} status))))
                    (js/setTimeout
                      (fn []
                        (swap! store assoc :subscription/exit-modal-shown true)
                        ;; Via the regular modal-open action, so that the
                        ;; modal's :on-load runs (it shuffles the reasons):
                        (event-handler {}
                                       [[:ui/modal-open
                                         :modals/subscription-exit-modal
                                         {}]]))
                      0)))))))}

     {:ui.action/kind :story-publish/set-highlight
      :ui.action/handler
      (fn [{:keys [store action]}]
        (let [[_ user-id highlight-id] action]
          (swap! store update :ui.story-publish.page/selected-accounts
                 (fn [selected]
                   (mapv (fn [entry]
                           (if (= (:instagram.user/id entry) user-id)
                             (if (seq highlight-id)
                               (assoc entry :highlight-id highlight-id)
                               (dissoc entry :highlight-id))
                             entry))
                         selected)))))}
     ]))
